import { Camera } from 'lucide-react'
import memoarLogo from '../assets/memoar-logo.png'

/**
 * Shown to a recipient whose view link does not resolve — unknown, revoked,
 * expired, or malformed. The API answers all four with an identical opaque
 * 404 so nobody can probe which tokens ever existed, and this screen holds
 * that line: it never names a cause. Rendered from both the view page's
 * `gone` state and the router's `/view/*` catch-all, so the two cannot drift.
 *
 * Voice: a closed link is not a failure, it is the product working — the
 * memories are private again, not lost. Copy says so in Ricordo's register
 * ("This story is waiting to be told", "Your memories await", "a forever
 * home"), because a stranger turned away from someone's keepsakes should
 * feel reassured, not error-messaged.
 *
 * It still never uses the words "expired" or "revoked". Naming both
 * possibilities would not actually reveal which one applied, but their absence
 * is what makes the no-oracle rule assertable, and a guard that can be tested
 * is worth more than a more specific sentence.
 */
export default function LinkUnavailable() {
  return (
    <main className="view-state link-gone">
      <img className="link-gone__mark" src={memoarLogo} alt="Memoar" />

      {/* The frame is the subject of the page, not an ornament. Under the glass
          sits a photograph with the interleaving tissue of an old album laid
          over it — the sheet you lift to see the picture underneath. Covered,
          not removed: the same thing the words say. */}
      <div className="link-gone__frame" aria-hidden="true">
        <div className="link-gone__mat">
          {/* Same Camera mark the album grid uses for a photo it can't show
              (album-list.tsx), softened as if read through the sheet. */}
          <Camera className="link-gone__subject" size={52} strokeWidth={1.25} />
          <div className="link-gone__tissue" />
        </div>
      </div>

      <h1 className="view-state__title">These memories are private again</h1>

      {/* Grouped so the two lines bind to each other more tightly than to the
          headline — reassurance first, then the one thing to do about it. */}
      <div className="link-gone__note">
        <p className="view-state__note">They&rsquo;re safe with the person who shared them.</p>
        <p className="view-state__note">
          Ask them to open a new link whenever you&rsquo;d like to see them again.
        </p>
      </div>
    </main>
  )
}
